import { Link } from 'react-router-dom'
import { NOT_MEASURED } from '../features/explore/fleetSummary'
import { advertisedHints } from '../lib/shortcuts'
import { useShortcutRegistry } from './Shortcuts'
import type { FleetCoverage } from '../lib/api'

/**
 * The pane down the left of a wide screen: saved views, coverage, and the keys that work.
 *
 * Coverage is here, on every screen, and that is the redesign's one editorial insistence. The gap
 * between a fleet that reports and a fleet that is covered is the single most important thing an
 * operator can forget, and a figure that only appears on a page somebody opens on Mondays is
 * forgotten by Tuesday. It reads a live endpoint rather than a projection precisely so it still
 * answers while a chart is stale.
 *
 * Hidden below 1120px rather than collapsed into something. At that width the content pane and the
 * inspector are already competing, and a third column would take its space from the table an operator
 * came to read.
 */
export interface SavedView {
  label: string
  to: string
  current: boolean
  /**
   * How many rows the view holds, where the screen can say so honestly.
   *
   * Optional, and absent rather than zero when it cannot be answered. The comment below this type used
   * to say the count "arrives when the API can answer it" - a plausible number beside a saved query on
   * a security console is exactly the failure D-263 exists to prevent. The ways-in endpoint answers it
   * fleet-wide; screens that still cannot leave it undefined and the pane prints nothing (D-450).
   */
  count?: number
}

function Bar({
  label,
  value,
  total,
  tone
}: {
  label: string
  value: number | null
  total: number | null
  tone: 'normal' | 'problem'
}) {
  // A measured zero out of a measured total is 0%. An unmeasured anything is no bar at all, because a
  // bar drawn at zero width is indistinguishable from a bar that is genuinely empty (D-263).
  const known = value !== null && total !== null && total > 0
  const percentage = known ? Math.round((value / total) * 100) : 0

  return (
    <div className="coverage-bar">
      <span className="coverage-line">
        <span>{label}</span>
        <span className={`tabular${tone === 'problem' ? ' problem' : ''}`}>
          {value === null || total === null ? NOT_MEASURED : `${value} / ${total}`}
        </span>
      </span>
      <span className="coverage-track" aria-hidden="true">
        {known && <span className={`coverage-fill${tone === 'problem' ? ' problem' : ''}`} style={{ width: `${percentage}%` }} />}
      </span>
    </div>
  )
}

export function ViewsPane({
  title,
  views,
  coverage
}: {
  title: string
  views: readonly SavedView[]
  coverage: FleetCoverage | null
}) {
  const { handlers } = useShortcutRegistry()

  // Every key the shell answers, and the rest only where this screen registered one. The same
  // list the keyboard shortcuts dialog prints, from the same function.
  const hints = advertisedHints(handlers)

  return (
    <aside className="views-pane" aria-label={title}>
      <span className="pane-eyebrow">{title}</span>

      {/*
        Saved views carry a label and nothing else. The design shows a count and a severity dot beside
        each, and `SavedQuerySummary` carries neither - it has a name, an expression and a run count.
        Rendering a plausible number next to a saved query on a security console is exactly the
        failure D-263 exists to prevent, so the count arrives when the API can answer it.
      */}
      <ul className="saved-views">
        {views.length === 0 && <li className="pane-empty">No saved views yet.</li>}
        {views.map((view) => (
          <li key={view.to}>
            {/* A router link rather than an anchor, so the organization segment in the address is
                kept; a bare href of /devices would reload the page and redirect to get it back. */}
            <Link to={view.to} className={view.current ? 'current' : undefined}>
              <span className="saved-view-label">{view.label}</span>
              {/* Absent rather than zero when the screen could not answer. A plausible number beside a
                  filter on a security console is the failure D-263 exists to prevent (D-450). */}
              {view.count !== undefined && (
                <span className="saved-view-count tabular">{view.count.toLocaleString()}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>

      <span className="pane-eyebrow">Coverage</span>
      <div className="coverage">
        <Bar
          label="Carrying a policy"
          value={coverage?.devicesCarryingPolicy ?? null}
          total={coverage?.devicesEnrolled ?? null}
          tone={coverage && coverage.devicesCarryingNoPolicy > 0 ? 'problem' : 'normal'}
        />
        <Bar
          label="Reporting"
          value={coverage?.devicesReporting ?? null}
          total={coverage?.devicesEnrolled ?? null}
          tone="normal"
        />
        <Bar
          label="Log verified"
          value={coverage?.auditChainsVerified ?? null}
          // Out of the devices that have ever submitted a log, not out of the fleet. A device no
          // audit policy applies to has not failed to verify anything, and putting it in the
          // denominator would report a shortfall nobody can close.
          total={coverage ? coverage.auditChainsVerified + coverage.auditChainsBroken : null}
          tone={coverage && coverage.auditChainsBroken > 0 ? 'problem' : 'normal'}
        />
      </div>

      {/*
        Only the bindings that work, and now only the ones that work *here*.

        The comment above this list always said a panel advertising a shortcut that does nothing costs
        the rest of the screen its credibility - and the panel was printing "Inspector · I" on eleven
        routes that had no inspector, because the shell dropped every action but the command bar. It is
        filtered by what the screen on view registered, so the claim is true per route rather than true
        on average (D-447).
      */}
      <span className="pane-eyebrow">Keys</span>
      <dl className="shortcut-hints">
        {hints.map((hint) => (
          <div key={hint.action}>
            <dt>{hint.what}</dt>
            <dd>
              <kbd>{hint.key}</kbd>
            </dd>
          </div>
        ))}
      </dl>
    </aside>
  )
}
